import React, { ReactElement, useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useSelector } from 'react-redux';
import { skipToken } from '@reduxjs/toolkit/query';
import {
  USER_SELECTABLE_WORKFLOW_STATUSES,
  WorkflowStatus,
} from '@enview/interface/types/triage/WorkflowStatus';
import { Priority } from '@enview/interface/types/triage/Priority';
import { Team } from '@enview/interface/types/Team';
import ArrowDown from '../../../components/svg/ArrowDown';
import Plus from '../../../components/svg/Plus';
import ConfirmActionPopover from '../Popovers/ConfirmActionPopover';
import WorkspaceSelectorContent from '../DropDowns/WorkspaceSelectorContent';
import TeamMemberSelectorContent from '../DropDowns/TeamMemberSelectorContent';
import { TeamAPI } from '../../../api';
import { getOrgForTeamMode } from '../../../dux';
import { State } from '../../../dux/@types';
import filteredTeamMembershipsForSelector from '../../../helpers/TeamHelper';
import { getWorkspaceColorClass } from '../../../helpers/WorkspaceColor';
import {
  getAvatarColorClass,
  getDisplayName,
  getInitials,
} from '../../../helpers/AvatarHelper';
import '../DropDowns/DropDown.scss';

// TEMPORARY: for a demo only. Dummy stand-ins for the real Tracking/Workspaces/
// Workflow Status/Assignee/Priority column-field components (TrackColumnField,
// WorkspacesColumnField, WorkflowStatusColumnField, AssigneeColumnField,
// PriorityColumnField), used only for the hardcoded regulation rows in
// demoRegulationRows.ts. Same markup, CSS classes, and open/select interaction as
// the real components, but every selection is held in local component state and
// never written anywhere — these rows don't correspond to a real bill on the
// backend, so there's nothing for a mutation to target. Remove this file along
// with the rest of the regulation proof-of-concept once the demo is over.

const useDemoUserWorkspaces = (): Team[] => {
  const organization = useSelector(getOrgForTeamMode);
  const orgId = organization?.id;
  const orgUser = useSelector((state: State) => state.account.organizationUser);
  const { data: teamMemberships } =
    TeamAPI.endpoints.getUsersTeamMemberships.useQuery(undefined);
  return filteredTeamMembershipsForSelector(teamMemberships, orgUser, orgId);
};

export const DemoTrackingField = (): ReactElement => {
  const userWorkspaces = useDemoUserWorkspaces();
  const [trackedTeamIds, setTrackedTeamIds] = useState<number[]>([]);
  const [show, setShow] = useState(false);
  const isTracked = trackedTeamIds.length > 0;

  const trigger = (
    <span className="tcf-trigger">
      <span
        className={`tcf-pill ${isTracked ? 'tcf-pill-tracking' : 'tcf-pill-track'}`}
      >
        <span className="tcf-dot" />
        <span className="tcf-label">{isTracked ? 'TRACKING' : 'TRACK'}</span>
      </span>
      <ArrowDown className="tcf-caret" />
    </span>
  );

  return (
    <span className="tcf-wrapper">
      <ConfirmActionPopover
        align="start"
        className="workspace-picker-popover"
        closePopup={() => setShow(false)}
        content={
          show ? (
            <WorkspaceSelectorContent
              billWorkspaceIds={trackedTeamIds}
              onSave={(teamIds) => {
                setTrackedTeamIds(teamIds);
                setShow(false);
              }}
              sectionLabel="TRACK INTO..."
              workspaces={userWorkspaces}
            />
          ) : null
        }
        hideCloseIcon
        onClickOutside={() => setShow(false)}
        positions={['bottom', 'top', 'right', 'left']}
        show={show}
        showArrowContainer={false}
        target={trigger}
        toggleCallback={() => setShow((prev) => !prev)}
      />
    </span>
  );
};

export const DemoWorkspacesField = (): ReactElement => {
  const userWorkspaces = useDemoUserWorkspaces();
  const [workspaceIds, setWorkspaceIds] = useState<number[]>([]);
  const [show, setShow] = useState(false);

  const selectedWorkspaces = userWorkspaces.filter((w) => workspaceIds.includes(w.id));

  let trigger: ReactElement;
  if (!selectedWorkspaces.length) {
    trigger = (
      <span className="wcf-trigger wcf-trigger-empty">
        <span className="wcf-empty-label">Add to workspace</span>
        <ArrowDown className="wcf-caret" />
      </span>
    );
  } else if (selectedWorkspaces.length === 1) {
    const [workspace] = selectedWorkspaces;
    trigger = (
      <span className="wcf-trigger">
        <span className={`wcf-dot ${getWorkspaceColorClass(workspace.id)}`} />
        <span className="wcf-name">{workspace.name}</span>
        <ArrowDown className="wcf-caret" />
      </span>
    );
  } else {
    trigger = (
      <span className="wcf-trigger">
        <span className="wcf-dot-stack">
          {selectedWorkspaces.slice(0, 3).map((workspace) => (
            <span
              className={`wcf-stacked-dot ${getWorkspaceColorClass(workspace.id)}`}
              key={workspace.id}
            />
          ))}
        </span>
        <span className="wcf-name">{selectedWorkspaces.length} workspaces</span>
        <ArrowDown className="wcf-caret" />
      </span>
    );
  }

  return (
    <ConfirmActionPopover
      align="start"
      className="workspace-picker-popover"
      closePopup={() => setShow(false)}
      content={
        show ? (
          <WorkspaceSelectorContent
            billWorkspaceIds={workspaceIds}
            onSave={(ids) => {
              setWorkspaceIds(ids);
              setShow(false);
            }}
            sectionLabel="WORKSPACES"
            workspaces={userWorkspaces}
          />
        ) : null
      }
      hideCloseIcon
      onClickOutside={() => setShow(false)}
      positions={['bottom', 'top', 'right', 'left']}
      show={show}
      showArrowContainer={false}
      target={trigger}
      toggleCallback={() => setShow((prev) => !prev)}
    />
  );
};

const statusClass = (status: WorkflowStatus): string =>
  status.toLowerCase().replace(/\s+/g, '-');

export const DemoWorkflowStatusField = (): ReactElement => {
  const [status, setStatus] = useState<WorkflowStatus | undefined>(undefined);
  const [displayed, setDisplayed] = useState(false);
  const [panelPosition, setPanelPosition] = useState({ top: 0, left: 0 });
  const triggerRef = useRef<HTMLDivElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);

  const updatePanelPosition = useCallback((): void => {
    if (!triggerRef.current) return;
    const rect = triggerRef.current.getBoundingClientRect();
    setPanelPosition({ top: rect.bottom + 4, left: rect.left });
  }, []);

  const handleToggle = (): void => {
    if (!displayed) updatePanelPosition();
    setDisplayed((prev) => !prev);
  };

  const handleClickOutside = useCallback((e: MouseEvent): void => {
    const target = e.target as Node;
    if (triggerRef.current?.contains(target) || panelRef.current?.contains(target)) {
      return;
    }
    setDisplayed(false);
  }, []);

  useEffect(() => {
    if (!displayed) return undefined;
    document.addEventListener('mousedown', handleClickOutside);
    window.addEventListener('scroll', updatePanelPosition, true);
    window.addEventListener('resize', updatePanelPosition);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      window.removeEventListener('scroll', updatePanelPosition, true);
      window.removeEventListener('resize', updatePanelPosition);
    };
  }, [displayed, handleClickOutside, updatePanelPosition]);

  return (
    <div className="workflow-status-field">
      <div className="wsf-control">
        {/* eslint-disable-next-line jsx-a11y/no-static-element-interactions, jsx-a11y/click-events-have-key-events */}
        <div
          className={`wsf-pill ${
            status ? `wsf-pill-${statusClass(status)}` : 'wsf-pill-unset'
          }`}
          onClick={handleToggle}
          ref={triggerRef}
        >
          <span className="wsf-pill-label">{status || 'Set status'}</span>
          <ArrowDown className="wsf-pill-caret" />
        </div>

        {displayed &&
          createPortal(
            <div
              className="wsf-dropdown-panel show"
              ref={panelRef}
              style={{
                position: 'fixed',
                top: panelPosition.top,
                left: panelPosition.left,
              }}
            >
              {USER_SELECTABLE_WORKFLOW_STATUSES.map((option) => (
                <button
                  className={`dropdown-item ${status === option ? 'checked' : ''}`}
                  key={option}
                  onClick={() => {
                    setStatus(option);
                    setDisplayed(false);
                  }}
                  type="button"
                >
                  <div className="dropdown-item-text">{option}</div>
                </button>
              ))}
            </div>,
            document.body,
          )}
      </div>
    </div>
  );
};

const priorityClass = (priority: Priority): string => priority.toLowerCase();

export const DemoPriorityField = (): ReactElement => {
  const [priority, setPriority] = useState<Priority | undefined>(undefined);
  const [displayed, setDisplayed] = useState(false);
  const [panelPosition, setPanelPosition] = useState({ top: 0, left: 0 });
  const triggerRef = useRef<HTMLDivElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);

  const updatePanelPosition = useCallback((): void => {
    if (!triggerRef.current) return;
    const rect = triggerRef.current.getBoundingClientRect();
    setPanelPosition({ top: rect.bottom + 4, left: rect.left });
  }, []);

  const handleToggle = (): void => {
    if (!displayed) updatePanelPosition();
    setDisplayed((prev) => !prev);
  };

  const handleClickOutside = useCallback((e: MouseEvent): void => {
    const target = e.target as Node;
    if (triggerRef.current?.contains(target) || panelRef.current?.contains(target)) {
      return;
    }
    setDisplayed(false);
  }, []);

  useEffect(() => {
    if (!displayed) return undefined;
    document.addEventListener('mousedown', handleClickOutside);
    window.addEventListener('scroll', updatePanelPosition, true);
    window.addEventListener('resize', updatePanelPosition);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      window.removeEventListener('scroll', updatePanelPosition, true);
      window.removeEventListener('resize', updatePanelPosition);
    };
  }, [displayed, handleClickOutside, updatePanelPosition]);

  return (
    <div className="priority-field">
      <div className="pf-control">
        {/* eslint-disable-next-line jsx-a11y/no-static-element-interactions, jsx-a11y/click-events-have-key-events */}
        <div
          className={`pf-pill ${
            priority ? `pf-pill-${priorityClass(priority)}` : 'pf-pill-unset'
          }`}
          onClick={handleToggle}
          ref={triggerRef}
        >
          <span className="pf-pill-label">{priority || 'Set priority'}</span>
          <ArrowDown className="pf-pill-caret" />
        </div>

        {displayed &&
          createPortal(
            <div
              className="pf-dropdown-panel show"
              ref={panelRef}
              style={{
                position: 'fixed',
                top: panelPosition.top,
                left: panelPosition.left,
              }}
            >
              {[Priority.High, Priority.Medium, Priority.Low].map((option) => (
                <button
                  className={`dropdown-item ${priority === option ? 'checked' : ''}`}
                  key={option}
                  onClick={() => {
                    setPriority(option);
                    setDisplayed(false);
                  }}
                  type="button"
                >
                  <div
                    className={`dropdown-item-text pf-option-${priorityClass(option)}`}
                  >
                    {option}
                  </div>
                </button>
              ))}
            </div>,
            document.body,
          )}
      </div>
    </div>
  );
};

export const DemoAssigneeField = (): ReactElement => {
  const userWorkspaces = useDemoUserWorkspaces();
  const primaryTeamId = userWorkspaces[0]?.id;
  const { data: teamMembers = [] } = TeamAPI.endpoints.getTeamMembers.useQuery(
    primaryTeamId ?? skipToken,
  );

  const [assigneeIds, setAssigneeIds] = useState<number[]>([]);
  const [displayed, setDisplayed] = useState(false);

  const assignedMembers = teamMembers.filter((member) =>
    assigneeIds.includes(member.organizationUserId),
  );
  const visibleMembers = assignedMembers.slice(0, 3);
  const overflowCount = assignedMembers.length - visibleMembers.length;

  const trigger = (
    <div className="asf-trigger">
      {assignedMembers.length === 0 ? (
        <span className="asf-assign-pill">
          <Plus /> Assign
        </span>
      ) : (
        <span className="asf-avatars">
          {visibleMembers.map((member) => (
            <span
              className={`asf-avatar ${getAvatarColorClass(member.organizationUserId)}`}
              key={member.organizationUserId}
              title={getDisplayName(member)}
            >
              {getInitials(member)}
            </span>
          ))}
          {overflowCount > 0 && (
            <span className="asf-avatar asf-avatar-overflow">+{overflowCount}</span>
          )}
        </span>
      )}
    </div>
  );

  return (
    <div className="assignee-field">
      <ConfirmActionPopover
        align="start"
        className="assignee-field-popover"
        closePopup={() => setDisplayed(false)}
        content={
          displayed ? (
            <TeamMemberSelectorContent
              assigneeIds={assigneeIds}
              members={teamMembers}
              onSave={(ids) => {
                setAssigneeIds(ids);
                setDisplayed(false);
              }}
            />
          ) : null
        }
        hideCloseIcon
        onClickOutside={() => setDisplayed(false)}
        positions={['bottom', 'top', 'right', 'left']}
        show={displayed}
        showArrowContainer={false}
        target={trigger}
        toggleCallback={() => setDisplayed((prev) => !prev)}
      />
    </div>
  );
};
